import { differenceInDays, startOfDay } from "date-fns";
import { useMemo } from "react";
import { parseUtcDate } from "@utils/date";
import { useFetchCurrentSubscriptionQuery } from "@hooks/queries/clinic/useFetchCurrentSubscriptionQuery";
import {
  CurrentSubscriptionStatus,
  SubscriptionPlanCode,
} from "@client/resources/CurrentSubscriptionResource";

export const useClinicSubscription = () => {
  const { currentSubscription: currentSubscriptionResponse, isLoading } =
    useFetchCurrentSubscriptionQuery();

  const subscriptionFromApi = useMemo(
    () => currentSubscriptionResponse ?? null,
    [currentSubscriptionResponse],
  );

  const subscription = useMemo(() => {
    if (!subscriptionFromApi) {
      return null;
    }

    return {
      uuid: subscriptionFromApi.uuid,
      plan: subscriptionFromApi.plan,
      name: subscriptionFromApi.plan?.name ?? "",
      code: subscriptionFromApi.plan?.code,
      createdAt: subscriptionFromApi.createdAt,
      startsAt: subscriptionFromApi.startsAt,
      endsAt: subscriptionFromApi.endsAt,
      currentUsagePeriod: subscriptionFromApi.currentUsagePeriod,
    };
  }, [subscriptionFromApi]);

  const daysRemaining = calculateDaysRemaining(subscription?.endsAt ?? null);

  return {
    subscription,
    hasSubscription: currentSubscriptionResponse !== null,
    hasValidStatus: isSubscriptionUsable(currentSubscriptionResponse),
    daysRemaining,
    startsAt: currentSubscriptionResponse?.startsAt ?? null,
    endsAt: currentSubscriptionResponse?.endsAt ?? null,
    isTrial: subscription?.code === SubscriptionPlanCode.TRIAL,
    isUnlimitedGrowth:
      subscription?.code === SubscriptionPlanCode.UNLIMITED_GROWTH,
    billingCycle: currentSubscriptionResponse?.billingCycle ?? null,
    status: currentSubscriptionResponse?.status ?? null,
    items: currentSubscriptionResponse?.items ?? [],
    nextBillingAt: currentSubscriptionResponse?.nextBillingAt ?? null,
    currentUsagePeriod: currentSubscriptionResponse?.currentUsagePeriod,
    currentPeriod: currentSubscriptionResponse?.currentPeriod ?? null,
    plan: currentSubscriptionResponse?.plan,
    cancelAtPeriodEnd: currentSubscriptionResponse?.cancelAtPeriodEnd ?? false,
    isLoading,
  };
};

const calculateDaysRemaining = (finishedAt: string | null): number | null => {
  if (!finishedAt) return null;

  const endDay = startOfDay(parseUtcDate(finishedAt));
  const todayStart = startOfDay(new Date());
  return differenceInDays(endDay, todayStart);
};

export const isSubscriptionUsable = (
  subscription: { status?: string | null } | null | undefined,
) =>
  Boolean(
    subscription?.status &&
    ALLOWED_CLINIC_STATUSES.includes(
      subscription.status as CurrentSubscriptionStatus,
    ),
  );

const ALLOWED_CLINIC_STATUSES = [
  CurrentSubscriptionStatus.Active,
  CurrentSubscriptionStatus.Trialing,
  CurrentSubscriptionStatus.PastDue,
];
